import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Eyebrow, Button } from "../components/Bits";
import { DEPARTMENTS } from "../lib/content";

function MemberCard({ m }) {
  return (
    <div className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-all duration-300 hover:border-nead-pink/40 hover:-translate-y-0.5" data-testid={`member-${m.name.toLowerCase().replace(/\s/g, "-")}`}>
      {m.photo ? (
        <div className="h-16 w-16 flex-none overflow-hidden rounded-2xl">
          <img src={m.photo} alt={m.name} className="h-full w-full object-cover object-top" />
        </div>
      ) : (
        <div className="flex h-16 w-16 flex-none items-center justify-center rounded-2xl grad-pink-cyan">
          <span className="font-display font-bold text-lg text-white tracking-tight">{m.initials}</span>
        </div>
      )}
      <div className="min-w-0">
        <h3 className="font-display font-bold text-lg leading-tight text-white">{m.name}</h3>
        <div className="text-sm text-white/55 leading-snug">{m.role}</div>
      </div>
    </div>
  );
}

export default function Team() {
  const [active, setActive] = useState(DEPARTMENTS[0].key);
  const dept = DEPARTMENTS.find((d) => d.key === active);

  return (
    <div>
      <PageHeader label="About NEAD" titleLines={["The people", "behind NEAD."]}
        intro="A dedicated team spanning leadership, festivals, youth work and advocacy. Choose a department to meet the people behind it." />

      <section className="bg-[#070912] text-white border-t border-white/10">
        <div className="container-x py-20 md:py-28">
          <div className="grid lg:grid-cols-[320px_1fr] gap-8 lg:gap-14">
            {/* Departments */}
            <div>
              <Eyebrow className="text-nead-cyan">Departments</Eyebrow>
              <div className="mt-6 flex flex-col gap-2" data-testid="dept-list">
                {DEPARTMENTS.map((d) => {
                  const on = d.key === active;
                  return (
                    <button
                      key={d.key}
                      onClick={() => setActive(d.key)}
                      data-testid={`dept-${d.key}`}
                      className={`group flex items-center justify-between rounded-2xl border px-5 py-4 text-left transition-all duration-300 ${on ? "border-nead-pink bg-nead-pink/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
                    >
                      <span className="min-w-0">
                        <span className={`font-display font-bold text-lg block leading-tight ${on ? "text-white" : "text-white/80"}`}>{d.name}</span>
                        <span className="text-xs text-white/45">{d.members.length} {d.members.length === 1 ? "person" : "people"}</span>
                      </span>
                      <ChevronRight className={`h-5 w-5 flex-none transition-all duration-300 ${on ? "text-nead-pink translate-x-0" : "text-white/30 -translate-x-1 group-hover:translate-x-0"}`} />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Members panel */}
            <div className="lg:border-l lg:border-white/10 lg:pl-14">
              <AnimatePresence mode="wait">
                <motion.div
                  key={dept.key}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35 }}
                  data-testid="dept-panel"
                >
                  <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight leading-[1.02]">{dept.name}</h2>
                  <p className="mt-4 max-w-xl text-white/60 leading-relaxed">{dept.blurb}</p>
                  <div className="mt-10 grid sm:grid-cols-2 gap-4">
                    {dept.members.map((m) => <MemberCard key={m.name + m.role} m={m} />)}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-x py-24 md:py-32 text-center">
        <Reveal>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-nead-navy max-w-2xl mx-auto leading-[1.05]">Join a team making a real difference.</h2>
          <p className="mt-5 text-nead-navy/60 max-w-xl mx-auto text-lg">We are always keen to hear from people who want to volunteer, partner or work with us.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Button to="/get-involved" variant="pink" testId="team-get-involved">Get involved</Button>
            <Button to="/contact" variant="outline" testId="team-contact">Contact us</Button>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
